import { MMKV } from "react-native-mmkv"

/**
 * In-memory fallback for SSR and iframe contexts where the MMKV native module
 * is absent (WEB-1H: "ThemeProvider: localStorage instance missing"). MMKV
 * instantiation throws synchronously in these environments, so we wrap it in
 * a try/catch and fall back to a minimal Map-backed shim that satisfies the
 * MMKV interface consumed by useMMKVString and the helper functions below.
 */
function createSafeStorage(): MMKV {
  try {
    return new MMKV()
  } catch {
    // SSR or iframe — native module not available. Return a functional shim.
    // The shim retains value-changed listeners and notifies them on set/delete/clearAll
    // so that useMMKVString re-renders when theme or other values change (WEB-1H).
    const memStore = new Map<string, string | number | boolean | Uint8Array>()
    const listeners = new Map<string, Set<(key: string) => void>>()

    const notify = (key: string) => {
      // Listeners are stored in the "__all__" global bucket (matching MMKV's
      // instance-wide addOnValueChangedListener behaviour).
      listeners.get("__all__")?.forEach((cb) => cb(key))
    }

    return {
      set: (key: string, value: string | number | boolean | Uint8Array) => {
        memStore.set(key, value)
        notify(key)
      },
      getString: (key: string) =>
        typeof memStore.get(key) === "string" ? (memStore.get(key) as string) : undefined,
      getNumber: (key: string) =>
        typeof memStore.get(key) === "number" ? (memStore.get(key) as number) : undefined,
      getBoolean: (key: string) =>
        typeof memStore.get(key) === "boolean" ? (memStore.get(key) as boolean) : undefined,
      getBuffer: (_key: string) => undefined,
      delete: (key: string) => {
        memStore.delete(key)
        notify(key)
      },
      clearAll: () => {
        const keys = Array.from(memStore.keys())
        memStore.clear()
        keys.forEach(notify)
      },
      contains: (key: string) => memStore.has(key),
      getAllKeys: () => Array.from(memStore.keys()),
      addOnValueChangedListener: (callback: (key: string) => void) => {
        // MMKV's addOnValueChangedListener fires for any key change; we use a
        // single "__all__" bucket to replicate that global behaviour in the shim.
        const bucket = "__all__"
        if (!listeners.has(bucket)) {
          listeners.set(bucket, new Set())
        }
        listeners.get(bucket)!.add(callback)
        return {
          remove: () => {
            listeners.get(bucket)?.delete(callback)
          },
        }
      },
      recrypt: () => {},
    } as unknown as MMKV
  }
}

export const storage = createSafeStorage()

/**
 * Loads a string from storage.
 *
 * @param key The key to fetch.
 */
export function loadString(key: string): string | null {
  try {
    return storage.getString(key) ?? null
  } catch {
    // not sure why this would fail... even reading the RN docs I'm unclear
    return null
  }
}

/**
 * Saves a string to storage.
 *
 * @param key The key to fetch.
 * @param value The value to store.
 */
export function saveString(key: string, value: string): boolean {
  try {
    storage.set(key, value)
    return true
  } catch {
    return false
  }
}

/**
 * Loads something from storage and runs it thru JSON.parse.
 *
 * @param key The key to fetch.
 */
export function load<T>(key: string): T | null {
  let almostThere: string | null = null
  try {
    almostThere = loadString(key)
    return JSON.parse(almostThere ?? "") as T
  } catch {
    return (almostThere as T) ?? null
  }
}

/**
 * Saves an object to storage.
 *
 * @param key The key to fetch.
 * @param value The value to store.
 */
export function save(key: string, value: unknown): boolean {
  try {
    saveString(key, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

/**
 * Removes something from storage.
 *
 * @param key The key to kill.
 */
export function remove(key: string): void {
  try {
    storage.delete(key)
  } catch {}
}

/**
 * Burn it all to the ground.
 */
export function clear(): void {
  try {
    storage.clearAll()
  } catch {}
}
